/**
 * Outing Detail Page - Organizer Command Center
 * Full management interface for a golf outing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Users, DollarSign, ClipboardList, Trophy,
  Settings, Send, Play, CheckCircle, AlertTriangle, LayoutGrid,
  Edit2, Link2, MapPin, Calendar
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Progress } from '../../../components/ui/progress';
import { toast } from 'sonner';

import { OutingProvider, useOuting } from '../context/OutingContext';
import { useOutingApi } from '../hooks/useOutingApi';
import InvitationManager from '../components/InvitationManager';
import TeamFormation from '../components/TeamFormation';
import PaymentTracker from '../components/PaymentTracker';
import ScorecardReview from '../components/ScorecardReview';
import OutingLeaderboard from '../components/OutingLeaderboard';
import GameSetupConfig from '../components/GameSetupConfig';
import TeamChat from '../components/TeamChat';
import {
  EditEventDetailsPopup,
  EditCoursePopup,
  EditGameFormatPopup,
  EditPrizesPopup,
  EditEntryFeePopup,
  ShareInviteLinkPopup
} from '../components/EditPopups';
import { 
  STATUS_CONFIG, formatDate, formatTime, formatCurrency,
  GAME_FORMATS, isOrganizer 
} from '../utils/outingHelpers';

function OutingDetailContent({ user }) {
  const { outingId } = useParams();
  const navigate = useNavigate();
  const api = useOutingApi();
  const { 
    currentOuting, teams, invitations, payments, scorecards,
    fetchOuting, fetchTeams, fetchInvitations, fetchPayments, fetchScorecards,
    loading 
  } = useOuting();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedTeamForChat, setSelectedTeamForChat] = useState(null);
  
  // Edit popup states
  const [editPopup, setEditPopup] = useState(null); // 'details', 'course', 'format', 'prizes', 'fee', 'share'

  // Load all data
  useEffect(() => {
    if (outingId) {
      fetchOuting(outingId);
      fetchTeams(outingId);
      fetchInvitations(outingId);
      fetchPayments(outingId);
      fetchScorecards(outingId);
    }
  }, [outingId, fetchOuting, fetchTeams, fetchInvitations, fetchPayments, fetchScorecards]);

  // Refresh data periodically during active games
  useEffect(() => {
    if (currentOuting?.status === 'in_progress') {
      const interval = setInterval(() => {
        fetchScorecards(outingId);
      }, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [currentOuting?.status, outingId, fetchScorecards]);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.updateOutingStatus(outingId, newStatus, user.user_id);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchOuting(outingId);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle saving edits from popups
  const handleSaveEdit = async (updates) => {
    try {
      await api.updateOuting(outingId, updates, user.user_id);
      toast.success('Changes saved!');
      fetchOuting(outingId);
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
      throw err;
    }
  };

  if (loading && !currentOuting) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!currentOuting) {
    return (
      <div className="ps-theme-page min-h-screen p-4">
        <p>Outing not found</p>
        <Button onClick={() => navigate('/outings')} className="mt-4">
          Back to Outings
        </Button>
      </div>
    );
  }

  const isUserOrganizer = isOrganizer(currentOuting, user?.user_id);
  const statusConfig = STATUS_CONFIG[currentOuting.status] || STATUS_CONFIG.draft;
  
  // Calculate stats
  const acceptedCount = invitations.filter(i => i.status === 'accepted').length;
  const totalCollected = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalDue = teams.reduce((sum, t) => sum + (t.total_due || 0), 0);
  const paymentProgress = totalDue > 0 ? (totalCollected / totalDue) * 100 : 0;
  const submittedScorecards = scorecards.filter(s => s.status !== 'pending').length;

  return (
    <div className="ps-theme-page min-h-screen" data-testid="outing-detail-theme-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] p-4 pb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/outings')}
            className="text-zinc-400 hover:text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black tracking-tight">{currentOuting.name}</h1>
              <Badge className={`bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400 border-${statusConfig.color}-500/50`}>
                {statusConfig.icon} {statusConfig.label}
              </Badge>
              {isUserOrganizer && (
                <button 
                  onClick={() => setEditPopup('details')}
                  className="p-1 hover:bg-white/10 rounded-full transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <button 
                onClick={() => isUserOrganizer && setEditPopup('course')}
                className={`flex items-center gap-1 ${isUserOrganizer ? 'hover:text-white cursor-pointer' : ''}`}
              >
                <MapPin className="w-3 h-3" />
                {currentOuting.course_name || 'Course TBD'}
              </button>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(currentOuting.event_date)} • {formatTime(currentOuting.tee_time)}
              </span>
            </div>
          </div>
          {isUserOrganizer && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditPopup('share')}
              className="text-[#007AFF] hover:bg-[#007AFF]/10"
            >
              <Link2 className="w-5 h-5" />
            </Button>
          )}
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3 text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-[#007AFF]" />
              <p className="text-xl font-bold">{acceptedCount}</p>
              <p className="text-xs text-zinc-400">Players</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3 text-center">
              <LayoutGrid className="w-5 h-5 mx-auto mb-1 text-purple-400" />
              <p className="text-xl font-bold">{teams.length}</p>
              <p className="text-xs text-zinc-400">Teams</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3 text-center">
              <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <p className="text-xl font-bold">{formatCurrency(totalCollected)}</p>
              <p className="text-xs text-zinc-400">Collected</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-3 text-center">
              <ClipboardList className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
              <p className="text-xl font-bold">{submittedScorecards}/{teams.length}</p>
              <p className="text-xs text-zinc-400">Scorecards</p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Status Actions (Organizer Only) */}
      {isUserOrganizer && (
        <div className="px-4 py-3 bg-[#121212] border-b border-white/10">
          <div className="flex items-center gap-2 overflow-x-auto">
            <span className="text-xs text-zinc-400 mr-2">Actions:</span>
            
            {currentOuting.status === 'draft' && (
              <Button 
                size="sm" 
                onClick={() => handleStatusChange('open')}
                disabled={updatingStatus}
                className="bg-green-600 hover:bg-green-500"
              >
                <Send className="w-3 h-3 mr-1" /> Open Invitations
              </Button>
            )}
            
            {currentOuting.status === 'open' && (
              <Button 
                size="sm" 
                onClick={() => handleStatusChange('locked')}
                disabled={updatingStatus}
                className="bg-yellow-600 hover:bg-yellow-500"
              >
                Lock Teams
              </Button>
            )}
            
            {currentOuting.status === 'locked' && (
              <Button 
                size="sm" 
                onClick={() => handleStatusChange('in_progress')}
                disabled={updatingStatus}
                className="bg-[#007AFF] hover:bg-[#38BDF8]"
              >
                <Play className="w-3 h-3 mr-1" /> Start Event
              </Button>
            )}
            
            {currentOuting.status === 'in_progress' && (
              <Button 
                size="sm" 
                onClick={() => handleStatusChange('review')}
                disabled={updatingStatus}
                className="bg-purple-600 hover:bg-purple-500"
              >
                End & Review
              </Button>
            )}
            
            {currentOuting.status === 'review' && (
              <Button 
                size="sm" 
                onClick={() => handleStatusChange('completed')}
                disabled={updatingStatus}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <CheckCircle className="w-3 h-3 mr-1" /> Complete Event
              </Button>
            )}
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/outings/${outingId}/leaderboard`)}
              className="border-white/20"
            >
              <Trophy className="w-3 h-3 mr-1" /> Live Board
            </Button>
          </div>
        </div>
      )}
      
      {/* Main Content Tabs */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1A1A1A] border border-white/10 w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#007AFF]">
              Overview
            </TabsTrigger>
            <TabsTrigger value="invitations" className="data-[state=active]:bg-[#007AFF]">
              Invitations
            </TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-[#007AFF]">
              Teams
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#007AFF]">
              Payments
            </TabsTrigger>
            <TabsTrigger value="scorecards" className="data-[state=active]:bg-[#007AFF]">
              Scorecards
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#007AFF]">
              Settings
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-4">
            <TabsContent value="overview">
              <OverviewTab 
                outing={currentOuting}
                teams={teams}
                invitations={invitations}
                payments={payments}
                scorecards={scorecards}
                totalCollected={totalCollected}
                totalDue={totalDue}
                paymentProgress={paymentProgress}
                isOrganizer={isUserOrganizer}
                onEdit={setEditPopup}
              />
            </TabsContent>
            
            <TabsContent value="invitations">
              <InvitationManager 
                outingId={outingId}
                user={user}
                onRefresh={() => fetchInvitations(outingId)}
              />
            </TabsContent>
            
            <TabsContent value="teams">
              <TeamFormation 
                outingId={outingId}
                user={user}
                teamSize={currentOuting.game_settings?.team_size || 4}
                entryFee={currentOuting.entry_fee}
                onRefresh={() => {
                  fetchTeams(outingId);
                  fetchInvitations(outingId);
                }}
                onSelectTeamChat={(team) => setSelectedTeamForChat(team)}
              />
              
              {/* Team Chat Section */}
              {selectedTeamForChat && (
                <div className="mt-6">
                  <TeamChat 
                    outingId={outingId}
                    teamId={selectedTeamForChat.id}
                    teamName={selectedTeamForChat.name}
                    user={user}
                    onClose={() => setSelectedTeamForChat(null)}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="payments">
              <PaymentTracker 
                outingId={outingId}
                user={user}
                onRefresh={() => {
                  fetchPayments(outingId);
                  fetchTeams(outingId);
                }}
              />
            </TabsContent>
            
            <TabsContent value="scorecards">
              <ScorecardReview 
                outingId={outingId}
                user={user}
                onRefresh={() => fetchScorecards(outingId)}
              />
            </TabsContent>
            
            <TabsContent value="settings">
              <GameSetupConfig 
                outing={currentOuting}
                user={user}
                onUpdate={() => fetchOuting(outingId)}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
      
      {/* Edit Popups */}
      {editPopup === 'details' && (
        <EditEventDetailsPopup
          outing={currentOuting}
          onSave={handleSaveEdit}
          onClose={() => setEditPopup(null)}
        />
      )}
      {editPopup === 'course' && (
        <EditCoursePopup
          outing={currentOuting}
          onSave={handleSaveEdit}
          onClose={() => setEditPopup(null)}
        />
      )}
      {editPopup === 'format' && (
        <EditGameFormatPopup
          outing={currentOuting}
          onSave={handleSaveEdit}
          onClose={() => setEditPopup(null)}
        />
      )}
      {editPopup === 'prizes' && (
        <EditPrizesPopup
          outing={currentOuting}
          onSave={handleSaveEdit}
          onClose={() => setEditPopup(null)}
        />
      )}
      {editPopup === 'fee' && (
        <EditEntryFeePopup
          outing={currentOuting}
          onSave={handleSaveEdit}
          onClose={() => setEditPopup(null)}
        />
      )}
      {editPopup === 'share' && (
        <ShareInviteLinkPopup
          outing={currentOuting}
          onClose={() => setEditPopup(null)}
        />
      )}
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ outing, teams, invitations, payments, scorecards, totalCollected, totalDue, paymentProgress, isOrganizer, onEdit }) {
  const front9Format = GAME_FORMATS[outing.game_settings?.front_9_format || outing.game_settings?.format];
  const back9Format = GAME_FORMATS[outing.game_settings?.back_9_format || outing.game_settings?.format];
  const isSplitFormat = !outing.game_settings?.same_format_all && 
    outing.game_settings?.front_9_format !== outing.game_settings?.back_9_format;
  
  // Calculate par totals
  const holePars = outing.hole_pars || [];
  const totalPar = holePars.reduce((a, b) => a + b, 0) || 72;
  
  return (
    <div className="space-y-4">
      {/* Event Info Card */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white text-lg">Event Details</CardTitle>
          {isOrganizer && (
            <button 
              onClick={() => onEdit('format')}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Edit2 className="w-4 h-4 text-zinc-400" />
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {outing.description && (
            <p className="text-zinc-300">{outing.description}</p>
          )}
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-zinc-400">Format</p>
              {isSplitFormat ? (
                <div className="space-y-1">
                  <p className="text-white font-semibold flex items-center gap-2">
                    {front9Format?.icon} {front9Format?.name} <span className="text-zinc-500 text-xs">(Front 9)</span>
                  </p>
                  <p className="text-purple-400 font-semibold flex items-center gap-2">
                    {back9Format?.icon} {back9Format?.name} <span className="text-zinc-500 text-xs">(Back 9)</span>
                  </p>
                </div>
              ) : (
                <p className="text-white font-semibold flex items-center gap-2">
                  {front9Format?.icon} {front9Format?.name || outing.game_settings?.format}
                </p>
              )}
            </div>
            <div>
              <p className="text-zinc-400">Team Size</p>
              <p className="text-white font-semibold">{outing.game_settings?.team_size || 4}-Person Teams</p>
            </div>
            <div>
              <p className="text-zinc-400">Entry Fee</p>
              <button 
                onClick={() => isOrganizer && onEdit('fee')}
                className={`text-white font-semibold ${isOrganizer ? 'hover:text-[#007AFF] cursor-pointer' : ''}`}
              >
                {formatCurrency(outing.entry_fee)}
                {isOrganizer && <Edit2 className="w-3 h-3 inline ml-1 text-zinc-500" />}
              </button>
            </div>
            <div>
              <p className="text-zinc-400">Course Par</p>
              <p className="text-white font-semibold">{totalPar}</p>
            </div>
          </div>
          
          {(outing.game_settings?.skins?.enabled || outing.game_settings?.ctp?.enabled) && (
            <div className="pt-3 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-zinc-400 text-sm">Side Games</p>
                {isOrganizer && (
                  <button 
                    onClick={() => onEdit('prizes')}
                    className="text-[#007AFF] text-xs flex items-center gap-1 hover:underline"
                  >
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {outing.game_settings?.skins?.enabled && (
                  <Badge className="bg-amber-500/20 text-amber-400">
                    Skins ${outing.game_settings.skins.total_pot}
                  </Badge>
                )}
                {outing.game_settings?.ctp?.enabled && (
                  <Badge className="bg-yellow-500/20 text-yellow-400">
                    CTP ${outing.game_settings.ctp.total_pot} (Holes {outing.game_settings.ctp.holes.join(', ')})
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {outing.game_settings?.custom_rules && (
            <div className="pt-3 border-t border-white/10">
              <p className="text-zinc-400 text-sm">Rules</p>
              <p className="text-white mt-1">{outing.game_settings.custom_rules}</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Payment Progress */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-lg">Payment Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-green-400">{formatCurrency(totalCollected)} collected</span>
            <span className="text-zinc-400">{formatCurrency(totalDue)} total</span>
          </div>
          <Progress value={paymentProgress} className="h-3" />
          {totalDue - totalCollected > 0 && (
            <p className="text-red-400 text-sm mt-2">
              {formatCurrency(totalDue - totalCollected)} outstanding
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <h4 className="text-zinc-400 text-sm mb-2">Invitations</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-300">Sent</span>
                <span className="text-white">{invitations.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400">Accepted</span>
                <span className="text-white">{invitations.filter(i => i.status === 'accepted').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-yellow-400">Pending</span>
                <span className="text-white">{invitations.filter(i => i.status === 'pending').length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <h4 className="text-zinc-400 text-sm mb-2">Scorecards</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-300">Teams</span>
                <span className="text-white">{teams.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400">Submitted</span>
                <span className="text-white">{scorecards.filter(s => s.status === 'submitted').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-emerald-400">Approved</span>
                <span className="text-white">{scorecards.filter(s => s.status === 'approved').length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Wrap with Provider
export default function OutingDetailPage({ user }) {
  return (
    <OutingProvider>
      <OutingDetailContent user={user} />
    </OutingProvider>
  );
}
